import { useTheme } from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import { GroupedPriceSeries } from "../types";
import { TFunction } from "i18next";
import { PriceSourceName } from "src/store/api/generatedApi";

interface PricesTableProps {
  priceSeries: GroupedPriceSeries;
  selectedDate: Date | null;
  selectedPriceSources: PriceSourceName[];
}

interface PriceRecord {
  materialName: string;
  priceSource: string;
  specificPrice: number;
}

type PriceRow = {
  // a price record that has been pivoted to have
  // columns for each price source
  id: number;
  materialName: string;
} & Record<string, number | string | null>;

export const PricesTable = ({
  priceSeries,
  selectedDate,
  selectedPriceSources,
}: PricesTableProps) => {
  const { t } = useTenantTranslation();
  const records = useMemo(
    () => buildRecords(priceSeries, selectedDate, selectedPriceSources),
    [priceSeries, selectedDate, selectedPriceSources]
  );

  const rows = useMemo(
    () =>
      buildRowsFromRecords(
        records,
        selectedPriceSources,
        priceSeries.availableMaterialNames
      ),
    [records, selectedPriceSources, priceSeries.availableMaterialNames]
  );

  const { columns, columnGroupingModel } = useBuildColumns(
    rows,
    selectedPriceSources,
    t
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        sx={defaultTableSx}
      />
    </div>
  );
};

const buildRecords = (
  priceSeries: GroupedPriceSeries,
  selectedDate: Date | null,
  selectedPriceSources: PriceSourceName[]
): PriceRecord[] => {
  const records: PriceRecord[] = [];

  for (const [key, series] of priceSeries.items.filter(([key]) =>
    selectedPriceSources.includes(key.priceSource)
  )) {
    // Find the index of the validFrom that is largest and less than or equal to selectedDate
    const filteredValidFroms = selectedDate
      ? series.validFrom.filter((validFrom) => validFrom.isBefore(selectedDate))
      : series.validFrom;

    if (filteredValidFroms.length === 0) {
      continue;
    }

    const maxValidFrom = filteredValidFroms.reduce((max, validFrom) =>
      validFrom.isAfter(max) ? validFrom : max
    );

    const index = series.validFrom.findIndex((validFrom) =>
      validFrom.isSame(maxValidFrom)
    );

    const specificPrice = series.specificPrice[index];

    if (specificPrice) {
      records.push({
        materialName: key.materialName,
        priceSource: key.priceSource,
        specificPrice: specificPrice,
      });
    }
  }

  return records;
};

const buildRowsFromRecords = (
  records: PriceRecord[],
  selectedPriceSources: PriceSourceName[],
  materialNames: string[]
): PriceRow[] =>
  materialNames.map((rowMaterial, index) => {
    const row: PriceRow = { id: index, materialName: rowMaterial };
    for (const columnPriceSource of selectedPriceSources) {
      const record = records.find(
        ({ materialName, priceSource }) =>
          materialName === rowMaterial && priceSource === columnPriceSource
      );
      row[columnPriceSource] = record?.specificPrice ?? null;
    }
    return row;
  });

const useBuildColumns = (
  rows: PriceRow[],

  selectedPriceSources: PriceSourceName[],
  t: TFunction
): {
  columns: GridColDef<PriceRow>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const theme = useTheme();
  const units = useUnitsFormatter(true);

  const formatNumber = useFormatNumber();

  const columns: GridColDef<PriceRow>[] = [
    { field: "materialName", headerName: t("material_name"), flex: 3 },
  ];

  const columnGroupingModel: GridColumnGroupingModel = [];
  const priceGroupChildren: { field: string }[] = [];

  // Find the maximum price value to use for bar scaling
  const maxPrice = rows.reduce((max, row) => {
    return Math.max(
      max,
      ...selectedPriceSources.map((source) => {
        const value = row[source];
        return typeof value === "number" ? value : 0;
      })
    );
  }, 0);

  // Create columns for each price source
  for (const source of selectedPriceSources) {
    columns.push({
      field: source,
      headerName: t(source),
      type: "number",
      flex: 2,
      align: "right",
      renderCell: (params: GridRenderCellParams<PriceRow, number | null>) => {
        if (params.value === null) return null;

        return !params.value ? null : (
          <BarCell
            value={params.value}
            formattedValue={formatNumber(2, true)(params.value)}
            // only use at most 2/3 the cell width for the bar
            maxValue={maxPrice * 1.5}
            color={theme.palette.data.blue}
          />
        );
      },
    });

    priceGroupChildren.push({ field: source });
  }

  // Add the column grouping model
  if (priceGroupChildren.length > 0) {
    columnGroupingModel.push({
      groupId: "price",
      headerName: `${t("price")} ${units("specific_cost")}`,
      children: priceGroupChildren,
    });
  }

  return { columns, columnGroupingModel };
};
